/**
 * Review period hook for listening module.
 *
 * Manages review period timer and auto-finish logic when timer expires.
 *
 * @module useReviewPeriod
 */

import { useEffect, useRef, useCallback } from 'react';

/**
 * Options for review period hook
 */
interface UseReviewPeriodOptions {
  /** Whether review period is active */
  isReviewPeriod: boolean;
  /** Time remaining in seconds */
  timeRemaining: number;
  /** Callback when review period timer expires */
  onExpire: () => void;
}

/**
 * Hook for managing review period timer and auto-finish.
 *
 * Automatically triggers finish callback when timer reaches zero.
 *
 * @param options - Review period configuration
 */
export function useReviewPeriod({
  isReviewPeriod,
  timeRemaining,
  onExpire,
}: UseReviewPeriodOptions) {
  const finishTriggeredRef = useRef(false);

  /**
   * Reset finish trigger when review period starts
   */
  useEffect(() => {
    if (isReviewPeriod) {
      finishTriggeredRef.current = false;
    }
  }, [isReviewPeriod]);

  /**
   * Auto-finish when review timer ends
   */
  useEffect(() => {
    if (!isReviewPeriod || timeRemaining > 0 || finishTriggeredRef.current) {
      return;
    }

    finishTriggeredRef.current = true;
    onExpire();
  }, [isReviewPeriod, timeRemaining, onExpire]);

  /**
   * Reset finish trigger when time remaining changes (new review period started)
   */
  useEffect(() => {
    if (timeRemaining > 0) {
      finishTriggeredRef.current = false;
    }
  }, [timeRemaining]);
}

